import { SignOutButton } from "@/components/SignOutButton";
import { Switch as WouterSwitch, Route, Redirect, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState, useEffect, useRef } from "react";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { ClientHeader } from "@/components/ClientHeader";
import Dashboard from "@/pages/Dashboard";
import GenericCategoryPage from "@/pages/GenericCategoryPage";
import PasswordTablePage from "@/pages/PasswordTablePage";
import OntarioFormsExportPage from "@/pages/OntarioFormsExportPage";
import NotFound from "@/pages/not-found";
import ExecutorProgressView from "@/pages/ExecutorProgressView";
import ReportDeathPage from "@/pages/ReportDeathPage";
import GracePeriodWaiting from "@/pages/GracePeriodWaiting";
import ExecutorPostDeathDashboard from "@/pages/ExecutorPostDeathDashboard";
import ExecutorEstateView from "@/pages/ExecutorEstateView";
import LetterGenerator from "@/pages/LetterGenerator";
import LetterPreviewPage from "@/pages/LetterPreviewPage";
import StatementScannerPage from "@/pages/StatementScannerPage";
import SettingsPage from "@/pages/SettingsPage";
import EstateSummaryPage from "@/pages/EstateSummaryPage";
import EstateDocumentsPage from "@/pages/EstateDocumentsPage";
// ORPH-121-1 (s121, fixed s200): ExecutorPostDeathDashboard mounts HERE as well as in
// ExecutorDashboard, but four of its nav targets were registered only there.
import ExecutorTaskList from "@/pages/executor/ExecutorTaskList";
import ExecutorRiskProfilePage from "@/pages/ExecutorRiskProfilePage";
import { WherePage } from "@/components/plan/WherePage";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { UpgradeModalProvider } from "@/contexts/UpgradeModalContext";
import { UpgradeModal } from "@/components/UpgradeModal";
import { Button } from "@/components/ui/button";
import { Users, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Client, InsertClientWithExecutors } from "@shared/schema";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { ClientFormDialog } from "@/components/ClientFormDialog";
import { AddCategoryDialog } from "@/components/AddCategoryDialog";
import { Footer } from "@/components/Footer";
import { useGuardedSignOut } from "@/components/LogoutSaveCheck";
import { SecureFileBanner } from "@/components/SecureFileBanner";
import { SecureFileLoadBanner } from "@/components/SecureFileLoadBanner";
import { SecureFileOpenProvider } from "@/components/SecureFileOpenProvider";

// s237 PR E (D-237.75): a planner who is also someone's executor reaches Where things are from the post-death
// dashboard's tile. The planner's name comes from the same contexts read that dashboard uses.
function ClientAsExecutorWhere({ clientId }: { clientId: string }) {
  const { data: contexts } = useQuery<{ executorFor: Array<{ clientId: string; plannerName?: string | null }> }>({
    queryKey: ["/api/user/contexts"],
  });
  const plannerName = contexts?.executorFor?.find((c) => c.clientId === clientId)?.plannerName;
  const planner = plannerName?.trim().split(/\s+/)[0] || "the plan owner";
  return <WherePage clientId={clientId} onOpen={() => {}} executor={{ planner }} />;
}

function ClientRootRedirect() {
  const [, setLocation] = useLocation();
  useEffect(() => { setLocation("/dashboard"); }, []);
  return null;
}

function Router({ clientData, onChangePassword }: { clientData: Client | null; onChangePassword: () => void }) {
  return (
    <WouterSwitch>
      {/* Own estate plan routes */}
      <Route path="/dashboard" component={() => <Dashboard clientData={clientData} onChangePassword={onChangePassword} showClientHeader={false} />} />
      {/* P3 (s87): the guided wizard was removed — redirect bookmarked /guide URLs to the dashboard. */}
      <Route path="/guide" component={() => <Redirect to="/dashboard" />} />
      <Route path="/guide/stage/:stage" component={() => <Redirect to="/dashboard" />} />
      <Route path="/category/:slug">{(params) => <GenericCategoryPage clientData={clientData} categorySlug={params.slug} />}</Route>
      {/* s81 (D-80.6): /executors dissolved into the "Your Executor" card under Legal. */}
      <Route path="/executors" component={() => <Redirect to="/category/legal?section=legal-your-executor" />} />
      {/* Children-render form (not component={() => …}) so SettingsPage keeps a stable
          element identity and is NOT remounted on every ClientDashboard re-render — a
          remount wipes in-progress local state (e.g. the MFA setup step). Matches the
          sibling routes below. */}
      <Route path="/settings">{() => <SettingsPage clientData={clientData} />}</Route>
      <Route path="/export/ontario-forms" component={() => <OntarioFormsExportPage clientData={clientData} />} />
      <Route path="/estate-summary" component={() => <EstateSummaryPage clientId={clientData?.id ?? ""} />} />
      {/* s217 — the PLANNER'S own scanner. Same page as the executor route below, but
          mode="planner", so the results table offers "Add to my plan" instead of the
          post-death cancel/transfer actions. The two mounts are deliberately separate
          routes rather than one route branching on who is looking: the executor route
          takes a :clientId for someone ELSE'S estate, this one is always your own. */}
      <Route path="/statement-scanner" component={() => <StatementScannerPage clientId={clientData?.id ?? ""} mode="planner" />} />
      {/* Executor views (for client users who are also named executors for others) */}
      <Route path="/executor/:clientId/progress">{(params) => <ExecutorProgressView clientId={params.clientId} />}</Route>
      <Route path="/executor/:clientId/report-death">{(params) => <ReportDeathPage clientId={params.clientId} />}</Route>
      <Route path="/executor/:clientId/waiting">{(params) => <GracePeriodWaiting clientId={params.clientId} />}</Route>
      <Route path="/executor/:clientId/dashboard">{(params) => <ExecutorPostDeathDashboard clientId={params.clientId} />}</Route>
      <Route path="/executor/:clientId/estate">{(params) => <ExecutorEstateView clientId={params.clientId} />}</Route>
      <Route path="/executor/:clientId/letters/:instanceKey">{(params) => <LetterPreviewPage clientId={params.clientId} instanceKey={decodeURIComponent(params.instanceKey)} />}</Route>
      <Route path="/executor/:clientId/letters">{(params) => <LetterGenerator clientId={params.clientId} previewMode={params.clientId === clientData?.id} />}</Route>
      <Route path="/executor/:clientId/statement-scanner">{(params) => <StatementScannerPage clientId={params.clientId} />}</Route>
      <Route path="/executor/:clientId/estate-documents">{(params) => <EstateDocumentsPage clientId={params.clientId} />}</Route>
      {/* ORPH-121-1 (s121 audit, fixed s200) — the client-as-executor dead-nav cluster.
          ExecutorPostDeathDashboard mounts above at /executor/:clientId/dashboard, but these four
          targets were registered ONLY in ExecutorDashboard, so a client-role tier-2 executor fell
          through to NotFound on all four. Mirrors ExecutorDashboard's registrations exactly,
          readOnly/isExecutor flags included — an executor must not get the planner's edit view.
          🔴 The register (deferred-builds.md) names the fourth member "checklist"; the component
          links "tasks" and never links a checklist route. Reported for the session close. */}
      <Route path="/executor/:clientId/tasks">{(params) => <ExecutorTaskList clientId={params.clientId} />}</Route>
      <Route path="/executor/:clientId/risk-profile">{(params) => <ExecutorRiskProfilePage clientId={params.clientId} />}</Route>
      <Route path="/executor/:clientId/estate-summary">{(params) => <EstateSummaryPage clientId={params.clientId} isExecutor={true} />}</Route>
      {/* s237 PR E (D-237.75): the post-death dashboard's "Where things are" tile — same page as ExecutorDashboard's route. */}
      <Route path="/executor/:clientId/where">{(params) => <ClientAsExecutorWhere clientId={params.clientId} />}</Route>
      <Route path="/client" component={ClientRootRedirect} />
      <Route component={NotFound} />
    </WouterSwitch>
  );
}

export function ClientDashboard() {
  const { logout, user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [editInfoOpen, setEditInfoOpen] = useState(false);
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  // Fetch contexts (myEstatePlan + any executor roles this user has)
  const { data: contexts } = useQuery<{ myEstatePlan: any; executorFor: any[] }>({
    queryKey: ["/api/user/contexts"],
  });
  const executorContexts = contexts?.executorFor ?? [];

  // Fetch the client's own data
  // User-scope the key so one account's cached client can't be read by another
  // after a session swap (Issue B, s84). Invalidations stay bare (prefix-match).
  // An explicit queryFn is REQUIRED: the default queryFn derives the URL from
  // queryKey.join("/"), so a 2-element key would fetch /api/clients/<userId>
  // (the by-id endpoint → 403) instead of the list. The key scopes the cache; the
  // queryFn pins the URL to the list endpoint.
  const { data: clients, isLoading } = useQuery<Client[]>({
    queryKey: ["/api/clients", user?.id],
    queryFn: async () => (await apiRequest("GET", "/api/clients")).json(),
  });

  const clientData = clients && clients.length > 0 ? clients[0] : null;
  const effectivePlannerTier = user?.isFAndF ? "complete" : (clientData?.plannerTier ?? "essential");

  // Mutation for updating client info
  const updateClientMutation = useMutation({
    mutationFn: async (data: Partial<InsertClientWithExecutors>) => {
      if (!clientData) throw new Error("No client data");
      const res = await apiRequest("PATCH", `/api/clients/${clientData.id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({
        title: "Success",
        description: "Your information has been updated.",
      });
      setEditInfoOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update information",
        variant: "destructive",
      });
    },
  });

  const addCategoryFlight = useSingleFlight();

  const addCategoryMutation = useMutation({
    mutationFn: async (data: { title: string; icon: string }) => {
      const slug = data.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      const res = await apiRequest("POST", "/api/generic-categories", {
        name: data.title,
        slug,
        icon: data.icon,
        categoryType: "custom",
        isDefaultTemplate: false,
        isActive: true,
        clientId: clientData?.id || null,
      });
      return res.json();
    },
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/unified/progress"] });
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories"] });
      toast({
        title: "Category added",
        description: `"${data.title}" has been added to your categories.`,
      });
      setAddCategoryOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding category",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Track previous mustChangePassword state to detect when password change completes
  const prevMustChangePassword = useRef(user?.mustChangePassword);
  
  // Auto-open password change dialog if user must change password
  useEffect(() => {
    if (user?.mustChangePassword) {
      setChangePasswordOpen(true);
    }
    
    // If password was just changed (went from true to false), refetch clients data
    if (prevMustChangePassword.current === true && user?.mustChangePassword === false) {
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
    }
    
    prevMustChangePassword.current = user?.mustChangePassword;
  }, [user?.mustChangePassword]);

  // s227 (LOGOUT-SAVE-CHECK-1): the account menu's Logout asks first when the Secure File has unsaved changes.
  const guardedSignOut = useGuardedSignOut();
  const handleLogout = () => guardedSignOut(async () => {
    try {
      await logout();
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
    } catch (error: any) {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // If user needs to change password, show the dialog first before checking for client data
  if (user?.mustChangePassword) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-2">Password Change Required</h2>
          <p className="text-muted-foreground mb-4">
            Please change your temporary password to continue.
          </p>
          <ChangePasswordDialog
            open={true}
            onOpenChange={setChangePasswordOpen}
            mustChange={true}
          />
        </div>
      </div>
    );
  }

  // Show loading state while fetching client data
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-2">Loading...</h2>
          <p className="text-muted-foreground">
            Please wait while we load your estate data.
          </p>
        </div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-2">No estate data found</h2>
          <p className="text-muted-foreground">
            Please contact your financial advisor to get started.
          </p>
        </div>
      </div>
    );
  }

  // s91: the standalone "Getting Started" onboarding questionnaire was retired —
  // About You (identity + situational toggles) + the per-section editors are now the
  // single guided surface, so new signups land straight on the dashboard (which owns
  // the 0%/first-visit experience). The onboarding_complete column + its endpoints +
  // seed were removed in the follow-up cleanup.

  return (
    // s227 (LOGOUT-SAVE-CHECK-1): SensitiveDataSessionProvider moved up to App (SecureFileSessionScope) so every
    // sign-out path, the interview's included, reads the same session.
    <SecureFileOpenProvider>
    <UpgradeModalProvider>
      <div className="flex h-screen w-full">
        <AppSidebar
          clientId={clientData.id}
          plannerTier={effectivePlannerTier}
          onNewClient={undefined}
          onAddCategory={() => setAddCategoryOpen(true)}
        />
        <div className="flex flex-col flex-1 min-w-0">
          <header className="flex items-center justify-between p-3 shrink-0 nav-header">
            <div className="flex items-center gap-2">
              <SidebarTrigger data-testid="button-sidebar-toggle" />
              <ClientHeader
                client={clientData}
                readOnly={false}
                compact={true}
                onEdit={() => setEditInfoOpen(true)}
                onChangePassword={() => setChangePasswordOpen(true)}
                onLogout={handleLogout}
              />
            </div>
            <div className="flex items-center gap-2">
              {executorContexts.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="button-context-switcher">
                      <Users className="w-4 h-4 mr-2" />
                      Switch View
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Your Views</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setLocation("/dashboard")}
                      data-testid="context-my-plan"
                    >
                      My Estate Plan
                    </DropdownMenuItem>
                    {executorContexts.map((ctx: any) => (
                      <DropdownMenuItem
                        key={ctx.clientId}
                        onClick={() => setLocation(`/executor/${ctx.clientId}/progress`)}
                        data-testid={`context-executor-${ctx.clientId}`}
                      >
                        Executor — {ctx.plannerName}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <SignOutButton />
            </div>
          </header>
          <SecureFileLoadBanner clientData={clientData} />
          <SecureFileBanner />
          {/* id: the planner scroll container — Dashboard reads it for scroll restoration (s152 Item 3). */}
          <main id="app-scroll-main" className="flex-1 overflow-y-auto overflow-x-hidden">
            <Router clientData={clientData} onChangePassword={() => setChangePasswordOpen(true)} />
          </main>
          <Footer />
        </div>

        <ChangePasswordDialog
          open={changePasswordOpen}
          onOpenChange={setChangePasswordOpen}
          mustChange={user?.mustChangePassword}
        />

        <AddCategoryDialog
          open={addCategoryOpen}
          onOpenChange={setAddCategoryOpen}
          onSubmit={(data) => addCategoryFlight.runQuiet(() => addCategoryMutation.mutateAsync(data))}
        />

        <ClientFormDialog
          open={editInfoOpen}
          onOpenChange={setEditInfoOpen}
          editMode={true}
          editData={clientData ? {
            name: clientData.name,
            email: clientData.email,
            phone: clientData.phone,
            address: clientData.address,
            city: clientData.city,
            state: clientData.state,
            zipCode: clientData.zipCode,
            country: clientData.country,
          } : undefined}
          onSubmit={(data) => updateClientMutation.mutate(data)}
          isPending={updateClientMutation.isPending}
        />

        <UpgradeModal />
      </div>
    </UpgradeModalProvider>
    </SecureFileOpenProvider>
  );
}
